import { Box, Modal, Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ModalStyle } from "../../../common/components/modalstyle/ModalStyle";
import { CloseModalIcon } from "../../../common/components/assets/commonassets/CommonAssets";
import CommonDivider from "../../../common/components/divider/CommonDivider";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CommonButton from "../../../common/components/Buttons/CommonButton";
import DatePickerField from "../../../common/components/FormFields/DatePickerFieldNew";
import TimePickerFieldNew from "../../../common/components/FormFields/TimePickerFieldNew";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import {
  errorAlert,
  successAlert,
  warningAlert,
} from "../../../common/components/Toasts/CustomToasts";
import CommonDynamicTableNew from "../../../common/components/CommonTable/CommonDynamicTableNew";
import { UseMilisecondsToDate } from "../../../common/components/Custom Hooks/UseMilisecondsToDate";
import { MdCancel } from "react-icons/md";
import {
  getACSTimeLineByPatientId,
  saveACSTimeline,
} from "../../services/acsServices/AcsTimeLineService";

// ─── Static Data ──────────────────────────────────────────────────────────────

const filledByERInitial = [
  { id: 1, srNo: 1, point: "Patient Arrival", time: null, date: null },
  { id: 2, srNo: 2, point: "Triage", time: null, date: null },
  { id: 3, srNo: 3, point: "Assessment", time: null, date: null },
  { id: 4, srNo: 4, point: "Onset of Symptoms", time: null, date: null },
  { id: 5, srNo: 5, point: "ECG", time: null, date: null },
  { id: 6, srNo: 6, point: "Aspirin", time: null, date: null },
  {
    id: 7,
    srNo: 7,
    point: "Counseling for Thrombolysis / Cath Lab",
    time: null,
    date: null,
  },
  {
    id: 8,
    srNo: 8,
    point: "Patient Transferred to CCU from Casualty",
    time: null,
    date: null,
  },
];

const filledByCCUInitial = [
  { id: 9, srNo: 9, point: "Consent Given Time", time: null, date: null },
  { id: 10, srNo: 10, point: "Consultant Seen", time: null, date: null },
  { id: 11, srNo: 11, point: "CCU / ICU Receiving", time: null, date: null },
  { id: 12, srNo: 12, point: "Thrombolysis", time: null, date: null },
  { id: 13, srNo: 13, point: "Balloon / Cath Lab", time: null, date: null },
];

const keyMap = {
  ///ER
  "Patient Arrival": "patientArr",
  Triage: "triage",
  Assessment: "assessment",
  "Onset of Symptoms": "onSetOfSymptoms",
  ECG: "ecg",
  Aspirin: "aspirin",
  "Counseling for Thrombolysis / Cath Lab": "counsellingForThromCath",
  "Patient Transferred to CCU from Casualty": "casualtyToIcuTransfer",
  //CCU
  "Consent Given Time": "consentGiven",
  "Consultant Seen": "consultantSeen",
  "CCU / ICU Receiving": "ccuIcuReceive",
  Thrombolysis: "thrombolysis",
  "Balloon / Cath Lab": "balloonCath",
};

// ─── Pure helpers (no closure over state) ────────────────────────────────────

const getMinDate = (data, index) => {
  const prevRow = data[index - 1];
  if (prevRow?.date) return new Date(prevRow.date);
  return null;
};

const getMinTime = (data, row, index) => {
  const selectedDate = row?.date ? new Date(row.date) : null;
  if (index === 0) return null;
  const prevRow = data[index - 1];
  if (!prevRow?.date || !prevRow?.time || !selectedDate) return null;
  const prevDate = new Date(prevRow.date);
  const sameDay =
    prevDate.getFullYear() === selectedDate.getFullYear() &&
    prevDate.getMonth() === selectedDate.getMonth() &&
    prevDate.getDate() === selectedDate.getDate();
  if (sameDay) {
    const minTime = new Date(prevRow.time);
    minTime.setMinutes(minTime.getMinutes() + 1);
    return minTime;
  }
  return null;
};

const combineDateTime = (date, time) => {
  if (!date || !time) return null;
  const d = new Date(date);
  const t = new Date(time);
  d.setHours(t.getHours());
  d.setMinutes(t.getMinutes());
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

// ─── Convert array → flat object for API payload ───────────────────────────
const convertArrayToObject = (dataArray) => {
  const formatKey = (text) =>
    text.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "");

  return dataArray.reduce((acc, row) => {
    const key = formatKey(row.point);
    acc[`${key}Date`] =
      row?.date !== null
        ? UseMilisecondsToDate(row?.date, "yyyy-MM-dd'T'HH:mm:ss")
        : null;
    acc[`${key}Time`] =
      row?.time !== null
        ? UseMilisecondsToDate(row?.time, "yyyy-MM-dd'T'HH:mm:ss")
        : null;
    return acc;
  }, {});
};

const isValidDateTime = (val) => val instanceof Date && !isNaN(val.getTime());

// ─── Component ────────────────────────────────────────────────────────────────

function ACSTimeLineModal(props) {
  const {
    openAcsTimeLineModal,
    setOpenAcsTimeLineModal,
    selectedRow,
    setOpenBackdrop,
  } = props;

  const defaultValues = {};
  const schema = yup.object().shape({});

  const {
    control,
    handleSubmit,
    reset,
    register,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues,
    // resolver: yupResolver(schema),
  });

  const [filledByERData, setFilledByERData] = useState(filledByERInitial);
  const [filledByCCUData, setFilledByCCUData] = useState(filledByCCUInitial);
  const [postConfirmationModal, setPostConfirmationModal] = useState(false);
  const [patientInfo, setPatientinfo] = useState(null);
  const [finalObject, setFinalObject] = useState(null);

  // ── Refs so callbacks always see the latest state without becoming stale ───
  const erDataRef = useRef(filledByERData);
  const ccuDataRef = useRef(filledByCCUData);
  useEffect(() => {
    erDataRef.current = filledByERData;
  }, [filledByERData]);
  useEffect(() => {
    ccuDataRef.current = filledByCCUData;
  }, [filledByCCUData]);

  // ── Patch RHF whenever state rows change (handles pre-fill from API) ────────
  // FIX 1: sync react-hook-form's internal store with state array values so
  //         DatePickerField / TimePickerFieldNew actually display the loaded data.
  useEffect(() => {
    filledByERData.forEach((row) => {
      setValue(`date_${row.id}`, row.date !== null ? new Date(row.date) : null);
      setValue(`time_${row.id}`, row.time !== null ? new Date(row.time) : null);
    });
  }, [filledByERData]);

  useEffect(() => {
    filledByCCUData.forEach((row) => {
      setValue(`date_${row.id}`, row.date !== null ? new Date(row.date) : null);
      setValue(`time_${row.id}`, row.time !== null ? new Date(row.time) : null);
    });
  }, [filledByCCUData]);

  // ─── Core date/time change handler ─────────────────────────────────────────
  // FIX 2: read the latest data from refs inside setState so we never close
  //         over stale state — this stops the render-loop / hang.
  const handleDateTimeChangeCommon = useCallback(
    ({
      type, // "date" | "time"
      value,
      row,
      index,
      section, // "ER" | "CCU"
    }) => {
      const setState =
        section === "ER" ? setFilledByERData : setFilledByCCUData;

      setState((prev) => {
        const updated = [...prev];
        const current = { ...updated[index] };
        const isFirstRow = index === 0;

        // ── STEP 1: validate input ───────────────────────────────────────────
        if (!isValidDateTime(value)) {
          setError(`${type}_${row.id}`, {
            type: "manual",
            message: `Invalid ${type}`,
          });
          return prev;
        }

        // ── STEP 2: previous row must be filled first ────────────────────────
        if (!isFirstRow) {
          const prevRow = updated[index - 1];
          if (!prevRow?.date || !prevRow?.time) {
            warningAlert(`Please fill "${prevRow.point}" first`);
            return prev;
          }
        }

        // ── STEP 3: prepare new values ───────────────────────────────────────
        let newDate = current.date;
        let newTime = current.time;

        if (type === "date") {
          newDate = value;
          newTime = null; // reset time whenever date changes
        } else {
          newTime = value;
        }

        const currentDateTime = combineDateTime(newDate, newTime);

        // ── STEP 4: sequence validation ──────────────────────────────────────
        if (!isFirstRow && currentDateTime) {
          const prevRow = updated[index - 1];
          const prevDateTime = combineDateTime(prevRow.date, prevRow.time);
          if (currentDateTime <= prevDateTime) {
            warningAlert(`"${row.point}" must be after "${prevRow.point}"`);
            setError(`time_${row.id}`, {
              type: "manual",
              message: "Invalid time sequence",
            });
            return prev;
          }
        }

        // ── STEP 5: update current row ───────────────────────────────────────
        current.date = newDate;
        current.time = newTime;

        if (type === "date") {
          setValue(`time_${row.id}`, null);
          setError(`time_${row.id}`, {
            type: "required",
            message: "Time is required",
          });
        } else {
          clearErrors(`time_${row.id}`);
        }

        updated[index] = current;

        // ── STEP 6: clear all subsequent rows ────────────────────────────────
        for (let i = index + 1; i < updated.length; i++) {
          updated[i] = { ...updated[i], date: null, time: null };
          setValue(`date_${updated[i].id}`, null);
          setValue(`time_${updated[i].id}`, null);
          clearErrors(`date_${updated[i].id}`);
          clearErrors(`time_${updated[i].id}`);
        }

        return [...updated];
      });
    },
    [],
  );

  // ─── Reset a row and everything after it ───────────────────────────────────
  const handleResetRow = useCallback((index, section) => {
    const setState = section === "ER" ? setFilledByERData : setFilledByCCUData;

    setState((prev) => {
      const updated = [...prev];
      for (let i = index; i < updated.length; i++) {
        updated[i] = { ...updated[i], date: null, time: null };
        setValue(`date_${updated[i].id}`, null);
        setValue(`time_${updated[i].id}`, null);
        clearErrors(`date_${updated[i].id}`);
        clearErrors(`time_${updated[i].id}`);
      }
      return updated;
    });
  }, []);

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const erRenderInput = useCallback(
    (row, index, header) => {
      // FIX 3: read prevFilled from the current snapshot passed in, not from a
      //         closure — avoids stale reads that caused inputs to stay disabled.
      const prevFilled =
        index === 0 ||
        (filledByERData[index - 1]?.date && filledByERData[index - 1]?.time);

      if (header === "time") {
        return (
          <div className="w-36">
            <TimePickerFieldNew
              key={row?.id}
              name={`time_${row.id}`}
              control={control}
              minTime={getMinTime(filledByERData, row, index)}
              disabled={!row?.date || !prevFilled}
              slotProps={{ textField: { size: "small" } }}
              error={errors?.[`time_${row.id}`]}
              inputRef={{
                ...register(`time_${row.id}`, {
                  onChange: (e) => {
                    const value = e?.target?.value;
                    handleDateTimeChangeCommon({
                      type: "time",
                      value,
                      row,
                      index,
                      section: "ER",
                    });
                  },
                }),
              }}
            />
          </div>
        );
      }

      if (header === "date") {
        return (
          <div className="flex gap-x-3">
            <div className="w-36">
              <DatePickerField
                key={row?.id}
                name={`date_${row.id}`}
                control={control}
                inputFormat="dd/MM/yyyy"
                minDate={getMinDate(filledByERData, index)}
                disableFuture={true}
                slotProps={{ textField: { size: "small" } }}
                disabled={!prevFilled}
                inputRef={{
                  ...register(`date_${row.id}`, {
                    onChange: (e) => {
                      const value = e?.target?.value;
                      handleDateTimeChangeCommon({
                        type: "date",
                        value,
                        row,
                        index,
                        section: "ER",
                      });
                    },
                  }),
                }}
              />
            </div>
            {row?.date !== null && (
              <Tooltip title="Reset" arrow>
                <button
                  type="button"
                  className="text-red-500 border border-red-500 bg-red-50 px-2 rounded-lg"
                  onClick={() => handleResetRow(index, "ER")}
                >
                  <MdCancel size={22} />
                </button>
              </Tooltip>
            )}
          </div>
        );
      }

      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      filledByERData,
      control,
      errors,
      register,
      handleDateTimeChangeCommon,
      handleResetRow,
    ],
  );

  const ccuRenderInput = useCallback(
    (row, index, header) => {
      const prevFilled =
        index === 0 ||
        (filledByCCUData[index - 1]?.date && filledByCCUData[index - 1]?.time);

      if (header === "time") {
        return (
          <div className="w-36">
            <TimePickerFieldNew
              key={row?.id}
              name={`time_${row.id}`}
              control={control}
              disabled={!row?.date || !prevFilled}
              minTime={getMinTime(filledByCCUData, row, index)}
              slotProps={{ textField: { size: "small" } }}
              error={errors?.[`time_${row.id}`]}
              inputRef={{
                ...register(`time_${row.id}`, {
                  onChange: (e) => {
                    const value = e?.target?.value;
                    handleDateTimeChangeCommon({
                      type: "time",
                      value,
                      row,
                      index,
                      section: "CCU",
                    });
                  },
                }),
              }}
            />
          </div>
        );
      }

      if (header === "date") {
        return (
          <div className="flex gap-x-2">
            <div className="w-36">
              <DatePickerField
                key={row?.id}
                name={`date_${row.id}`}
                control={control}
                inputFormat="dd/MM/yyyy"
                disableFuture={true}
                minDate={getMinDate(filledByCCUData, index)}
                slotProps={{ textField: { size: "small" } }}
                disabled={!prevFilled}
                inputRef={{
                  ...register(`date_${row.id}`, {
                    onChange: (e) => {
                      const value = e?.target?.value;
                      handleDateTimeChangeCommon({
                        type: "date",
                        value,
                        row,
                        index,
                        section: "CCU",
                      });
                    },
                  }),
                }}
              />
            </div>
            {row?.date !== null && (
              <Tooltip title="Reset" arrow>
                <button
                  type="button"
                  className="text-sky-500 border border-sky-500 bg-sky-50 px-2 rounded-lg"
                  onClick={() => handleResetRow(index, "CCU")}
                >
                  <MdCancel size={22} />
                </button>
              </Tooltip>
            )}
          </div>
        );
      }

      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      filledByCCUData,
      control,
      errors,
      register,
      handleDateTimeChangeCommon,
      handleResetRow,
    ],
  );

  // ─── Save click ─────────────────────────────────────────────────────────────
  const handleSaveClick = () => {
    let hasError = false;
    [...filledByERData, ...filledByCCUData].forEach((row) => {
      if (row?.date !== null && row?.time === null) {
        setError(`time_${row.id}`, {
          type: "manual",
          message: "Time is required",
        });
        warningAlert("Please select time for selected date");
        hasError = true;
      }
    });
    if (hasError) return;

    const erObject = convertArrayToObject(filledByERData);
    const ccuObject = convertArrayToObject(filledByCCUData);

    const tempObj = {
      emergencyId: selectedRow?.EmergencyPatientId,
      // ER keys
      patientArrDate: erObject?.PatientArrivalDate,
      patientArrTime: erObject?.PatientArrivalTime,
      triageDate: erObject?.TriageDate,
      triageTime: erObject?.TriageTime,
      assessmentDate: erObject?.AssessmentDate,
      assessmentTime: erObject?.AssessmentTime,
      onSetOfSymptomsDate: erObject?.OnsetofSymptomsDate,
      onSetOfSymptomsTime: erObject?.OnsetofSymptomsTime,
      ecgDate: erObject?.ECGDate,
      ecgTime: erObject?.ECGTime,
      aspirinDate: erObject?.AspirinDate,
      aspirinTime: erObject?.AspirinTime,
      counsellingForThromCathDate:
        erObject?.CounselingforThrombolysisCathLabDate,
      counsellingForThromCathTime:
        erObject?.CounselingforThrombolysisCathLabTime,
      casualtyToIcuTransferDate:
        erObject?.PatientTransferredtoCCUfromCasualtyDate,
      casualtyToIcuTransferTime:
        erObject?.PatientTransferredtoCCUfromCasualtyTime,
      // CCU keys
      consentGivenDate: ccuObject?.ConsentGivenTimeDate,
      consentGivenTime: ccuObject?.ConsentGivenTimeTime,
      consultantSeenDate: ccuObject?.ConsultantSeenDate,
      consultantSeenTime: ccuObject?.ConsultantSeenTime,
      ccuIcuReceiveDate: ccuObject?.CCUICUReceivingDate,
      ccuIcuReceiveTime: ccuObject?.CCUICUReceivingTime,
      thrombolysisDate: ccuObject?.ThrombolysisDate,
      thrombolysisTime: ccuObject?.ThrombolysisTime,
      balloonCathDate: ccuObject?.BalloonCathLabDate,
      balloonCathTime: ccuObject?.BalloonCathLabTime,
    };

    setFinalObject(tempObj);
    setPostConfirmationModal(true);
  };

  // ─── Confirm & submit ───────────────────────────────────────────────────────
  const onSubmit = () => {
    setPostConfirmationModal(false);
    setOpenBackdrop(true);
    saveACSTimeline(finalObject)
      .then((res) => res.data)
      .then((res) => {
        if (res.statusCode === 200 || res.statusCode === 201) {
          successAlert(res?.message);
          handleReset();
          setOpenAcsTimeLineModal(false);
        } else {
          errorAlert(res?.message);
        }
        setOpenBackdrop(false);
      })
      .catch((err) => {
        errorAlert(err?.message);
        setOpenBackdrop(false);
      });
  };

  const handleReset = () => {
    reset(defaultValues);
    setFilledByERData(filledByERInitial);
    setFilledByCCUData(filledByCCUInitial);
    setFinalObject(null);
  };

  // ─── Load existing timeline on open ────────────────────────────────────────
  // FIX 4: after setting state, the two useEffects above will run and call
  //         setValue for every field — so the date/time pickers will display
  //         the fetched values automatically. No manual setValue loop needed here.
  useEffect(() => {
    if (selectedRow !== null) {
      console.log("selectedRow", selectedRow);

      setPatientinfo({
        "Patient Name": selectedRow?.["Patient Name"],
        "Mr.No": selectedRow?.MRNo,
        Gender: selectedRow?.Gender,
        Age: selectedRow?.Age,
        "Doctor Name": selectedRow?.["Doctor Name"],
      });

      getACSTimeLineByPatientId(selectedRow?.EmergencyPatientId)
        .then((response) => response.data)
        .then((res) => {
          let result = res?.result;
          // Patch ER rows from API response
          setFilledByERData((prev) =>
            prev.map((item) => {
              const key = keyMap[item.point];
              return {
                ...item,
                date: key ? result?.[`${key}Date`] : null,
                time: key ? result?.[`${key}Time`] : null,
              };
            }),
          );
          setFilledByCCUData((prev) =>
            prev.map((item) => {
              const key = keyMap[item.point];
              return {
                ...item,
                date: key ? result?.[`${key}Date`] : null,
                time: key ? result?.[`${key}Time`] : null,
              };
            }),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={openAcsTimeLineModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: "90%",
          maxHeight: "80%",
          bgcolor: "background.paper",
          border: "1px solid gray",
          overflowY: "scroll",
          boxShadow: 20,
          p: 2,
        }}
        className="w-[97%] max-h-[94%]"
      >
        {/* ══ Modal Header ══════════════════════════════════════════════════ */}
        <div className="flex justify-between items-center pb-1">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3 mt-0.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <div>
              <h1 className="font-bold text-[18px] text-gray-800 leading-tight">
                ACS Timeline
              </h1>
            </div>
          </div>
          <CloseModalIcon
            type="button"
            onClick={() => setOpenAcsTimeLineModal(false)}
          />
        </div>

        <CommonDivider color="#E5E7EB" height={1} />

        {patientInfo !== null && (
          <div className="rounded-md shadow bg-blue-100 border border-blue-500 p-2 my-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 text-sm">
              {Object.entries(patientInfo)?.map(([key, value]) => (
                <div
                  key={key}
                  className="grid grid-cols-[40%_3%_56%] lg:grid-cols-[35%_3%_63%] xl:grid-cols-[20%_3%_77%]"
                >
                  <div className="font-semibold text-gray-600 whitespace-nowrap">
                    {key}
                  </div>
                  <div className="font-semibold text-[#073763]">:</div>
                  <div className="font-semibold text-customBlack break-words">
                    {value || "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ Form ══════════════════════════════════════════════════════════ */}
        <form
          onSubmit={handleSubmit(handleSaveClick)}
          className="mt-4 space-y-5"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* ── Section 1 : ER ──────────────────────────────────────────── */}
            <div className="rounded-xl border border-red-400 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-200 via-rose-200 to-red-300 border-b border-red-200">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center shadow-md flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[14px] text-red-800 leading-tight">
                      To Be Filled by ER
                    </h2>
                    <p className="text-[11px] text-red-600 leading-none mt-0.5">
                      Emergency Room
                    </p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-red-500 text-white shadow-sm tracking-wide">
                  8 Milestones
                </span>
              </div>
              <div className="bg-white">
                {filledByERData.length > 0 ? (
                  <CommonDynamicTableNew
                    dataResult={filledByERData}
                    highlightRow={false}
                    tableClass="capitalize max-h-[420px] -my-2"
                    renderInput={erRenderInput}
                    editableColumns={["time", "date"]}
                    removeHeaders={["id", "srNo"]}
                  />
                ) : (
                  <p className="py-10 text-center text-gray-400 text-sm">
                    No ER milestones found.
                  </p>
                )}
              </div>
            </div>

            {/* ── Section 2 : CCU ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-blue-400 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-200 via-sky-200 to-blue-300 border-b border-blue-200">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shadow-md flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[14px] text-blue-800 leading-tight">
                      To Be Filled by CCU
                    </h2>
                    <p className="text-[11px] text-blue-600 leading-none mt-0.5">
                      Cardiac Care Unit
                    </p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-blue-500 text-white shadow-sm tracking-wide">
                  5 Milestones
                </span>
              </div>
              <div className="bg-white">
                {filledByCCUData.length > 0 ? (
                  <CommonDynamicTableNew
                    dataResult={filledByCCUData}
                    highlightRow={false}
                    tableClass="capitalize max-h-[400px] -my-2"
                    renderInput={ccuRenderInput}
                    editableColumns={["time", "date"]}
                    removeHeaders={["id", "srNo"]}
                  />
                ) : (
                  <p className="py-10 text-center text-gray-400 text-sm">
                    No CCU milestones found.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end items-center gap-3 pb-1">
            <CommonButton
              type="button"
              label="Reset"
              className="resetButton text-customRed border border-customRed"
              onClick={handleReset}
            />
            <CommonButton
              type="submit"
              label="Save"
              className="saveButton bg-customGreen text-white"
            />
          </div>
        </form>

        {/* ── Confirmation Modal ───────────────────────────────────────────── */}
        {postConfirmationModal && (
          <ConfirmationModal
            confirmationOpen={postConfirmationModal}
            confirmationHandleClose={() => setPostConfirmationModal(false)}
            confirmationSubmitFunc={onSubmit}
            confirmationMsg="Are you sure you want to save the ACS TAT record?"
            confirmationButtonMsg="Confirm"
            confirmationLabel="Confirmation"
          />
        )}
      </Box>
    </Modal>
  );
}

export default ACSTimeLineModal;
